import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useMenu } from '../api/restaurant';
import { CATEGORIES, CATEGORY_HINT, CATEGORY_LABEL, formatQty, unitWord, UNIT_LABEL, useIngredients, useMovements, useRecipe, useReorderList, useStockMutation, type Ingredient, type ReorderItem, type StockCategory, type StockMovement, type StockUnit } from '../api/stock';
import { formatDate, formatDateTime } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

const LEVEL = { OK: { label: 'In stock', tone: 'good' as const }, LOW: { label: 'Running low', tone: 'warn' as const }, OUT: { label: 'Out', tone: 'bad' as const }, NEW: { label: 'Not counted yet', tone: '' as const } };
const REASON: Record<StockMovement['reason'], string> = { RECEIPT: 'Delivery', SALE: 'Used in an order', RETURN: 'Not made: put back', WASTE: 'Waste, broken or lost', ADJUSTMENT: 'Stock count', USE: 'Used or issued' };
/** Words people count things in, offered while typing; anything else can be typed. */
const WORDS = ['pieces', 'sets', 'pairs', 'boxes', 'packs', 'bottles', 'rolls', 'books', 'reams', 'bars', 'sachets'];
const whole = (s: string) => (s.trim() === '' ? 0 : /^\d+$/.test(s.trim()) ? Number(s.trim()) : null);

type Move = 'RECEIPT' | 'USE' | 'WASTE' | 'ADJUSTMENT';

export function Stock() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<'stock' | 'buy' | 'recipes'>(params.get('tab') === 'buy' ? 'buy' : 'stock');
  const reorder = useReorderList();
  const toBuy = reorder.data?.items.length ?? 0;
  return (
    <div className="page">
      <PageHead eyebrow="Hotel, kitchen and bar" title="Inventory" />
      <div className="seg no-print" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'stock'} onClick={() => setTab('stock')}>Items</button>
        <button type="button" aria-pressed={tab === 'buy'} onClick={() => setTab('buy')}>Buy list{toBuy > 0 ? ` (${toBuy})` : ''}</button>
        {can('stock.manage') ? <button type="button" aria-pressed={tab === 'recipes'} onClick={() => setTab('recipes')}>Recipes</button> : null}
      </div>
      {tab === 'stock' ? <ItemsTab /> : tab === 'buy' ? <BuyList /> : <RecipesTab />}
    </div>
  );
}

/* ---------------------------------------------------------------- buy list */
const piece = (n: number, i: { unit: StockUnit; unitName: string | null }) => formatQty(n, i.unit, i.unitName);

/** Everything out or at its reorder level, most urgent first: to print, or to copy into a message to the supplier. */
function BuyList() {
  const { me } = useAuth();
  const toast = useToast();
  const q = useReorderList();
  const [text, setText] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  const today = me?.property.today ?? '';

  const line = (i: ReorderItem) => `${i.name}: ${i.suggested !== null ? piece(i.suggested, i) : '(how many?)'}  (have ${piece(i.onHand, i)}${i.location ? `, kept in ${i.location}` : ''})`;
  const asText = () => {
    const out = items.filter((i) => i.state === 'OUT');
    const low = items.filter((i) => i.state === 'LOW');
    return [
      `${me?.property.name ?? 'Buy list'}: buy list, ${today ? formatDate(today, true) : ''}`,
      ...(out.length ? ['', 'OUT OF STOCK', ...out.map((i) => `- ${line(i)}`)] : []),
      ...(low.length ? ['', 'RUNNING LOW', ...low.map((i) => `- ${line(i)}`)] : []),
    ].join('\n');
  };
  async function copy() {
    const t = asText();
    try {
      await navigator.clipboard.writeText(t);
      toast.ok('Copied. Paste it into WhatsApp or a message.');
    } catch {
      setText(t); // no clipboard here: show the text to select and copy by hand
    }
  }

  if (q.isLoading) return <Loading rows={3} />;
  if (q.error) return <ErrorNote error={q.error} retry={() => void q.refetch()} />;

  const group = (state: 'OUT' | 'LOW', title: string) => {
    const rows = items.filter((i) => i.state === state);
    if (rows.length === 0) return null;
    return (
      <section aria-label={title} className="stack">
        <h2 className="h2">{title} ({rows.length})</h2>
        <div className="list">
          {rows.map((i) => (
            <div key={i.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span className="title">{i.name}</span>
                <span className="num">{i.suggested !== null ? <>Buy about <strong>{piece(i.suggested, i)}</strong></> : <span className="muted">How many?</span>}</span>
              </div>
              <div className="row small muted">
                <span>have {piece(i.onHand, i)}</span>
                {i.reorderLevel > 0 ? <span>· reorder at {piece(i.reorderLevel, i)}</span> : null}
                <span>· {CATEGORY_LABEL[i.category]}</span>
                {i.location ? <span>· kept in {i.location}</span> : null}
                {i.usualDelivery !== null ? <span>· usual delivery {piece(i.usualDelivery, i)}{i.lastDelivered ? `, last ${formatDateTime(i.lastDelivered)}` : ''}</span> : null}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="stack">
      <div className="row no-print">
        <Button variant="primary" onClick={() => window.print()} disabled={items.length === 0}>Print</Button>
        <Button onClick={() => void copy()} disabled={items.length === 0}>Copy as text</Button>
      </div>
      <p className="small muted print-only-title">{me?.property.name}: buy list, {today ? formatDate(today, true) : ''}</p>
      {text ? (
        <Field id="bl-text" label="Select all of this and copy it" hint="Copying did not work on this device">
          <textarea id="bl-text" className="input" readOnly rows={Math.min(14, text.split('\n').length + 1)} value={text} onFocus={(e) => e.currentTarget.select()} />
        </Field>
      ) : null}
      {items.length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="Nothing to buy">Everything that has been counted is above its reorder level.{(q.data?.notCounted ?? 0) > 0 ? ` ${q.data!.notCounted} item${q.data!.notCounted === 1 ? ' has' : 's have'} not been counted yet: count them so they can appear here when they run low.` : ''}</Empty>
        </div>
      ) : (
        <>
          {group('OUT', 'Out of stock')}
          {group('LOW', 'Running low')}
          <p className="small muted">The quantity is the usual delivery size, and never less than what gets the item back to twice its reorder level. Items with no reorder level set only appear once they are out. {(q.data?.notCounted ?? 0) > 0 ? `${q.data!.notCounted} item${q.data!.notCounted === 1 ? ' is' : 's are'} not counted yet and not on this list.` : ''}</p>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- items */
function ItemsTab() {
  const { can } = useAuth();
  const list = useIngredients();
  const [adding, setAdding] = useState(false);
  const [move, setMove] = useState<{ ing: Ingredient; reason: Move } | null>(null);
  const [history, setHistory] = useState<Ingredient | null>(null);
  const [editing, setEditing] = useState<Ingredient | null>(null);
  const [category, setCategory] = useState<StockCategory | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const all = list.data ?? [];

  const counts = useMemo(() => new Map(CATEGORIES.map((c) => [c, all.filter((i) => i.active && i.category === c).length])), [all]);
  const rows = all.filter((i) => i.active && (category === 'ALL' || i.category === category) && (search.trim() === '' || `${i.name} ${i.location ?? ''}`.toLowerCase().includes(search.trim().toLowerCase())));
  const low = rows.filter((i) => i.level === 'LOW').length;
  const out = rows.filter((i) => i.level === 'OUT').length;
  const fresh = rows.filter((i) => i.level === 'NEW').length;

  return (
    <>
      {can('stock.manage') ? (
        <div className="row">
          <Button variant="primary" onClick={() => setAdding(true)}>Add an item</Button>
        </div>
      ) : null}
      {all.length > 0 ? (
        <div className="grid-2">
          <Field id="inv-cat" label="Kind">
            <select id="inv-cat" className="select" value={category} onChange={(e) => setCategory(e.target.value as StockCategory | 'ALL')}>
              <option value="ALL">Everything ({all.filter((i) => i.active).length})</option>
              {CATEGORIES.filter((c) => (counts.get(c) ?? 0) > 0).map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]} ({counts.get(c)})
                </option>
              ))}
            </select>
          </Field>
          <Field id="inv-q" label="Search">
            <input id="inv-q" className="input" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or where it is kept" />
          </Field>
        </div>
      ) : null}
      {rows.length > 0 ? (
        <p className="small muted" role="status">
          {rows.length} item{rows.length === 1 ? '' : 's'}
          {out ? ` · ${out} out` : ''}
          {low ? ` · ${low} running low` : ''}
          {fresh ? ` · ${fresh} not counted yet` : ''}
        </p>
      ) : null}

      {list.isLoading ? (
        <Loading rows={3} />
      ) : list.error ? (
        <ErrorNote error={list.error} retry={() => void list.refetch()} />
      ) : all.length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="Nothing here yet">Add what the hotel keeps count of: bed linen, towels, glasses, plates, cleaning supplies, and the kitchen's and bar's ingredients. For kitchen items, say how much each dish needs under Recipes, and stock comes off as orders are sent.</Empty>
        </div>
      ) : rows.length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="No items match" />
        </div>
      ) : (
        <div className="list">
          {rows.map((i) => (
            <div key={i.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{i.name}</span>
                <Badge tone={LEVEL[i.level].tone}>{LEVEL[i.level].label}</Badge>
              </div>
              <div className="row small muted">
                <span className="num">{i.counted ? formatQty(i.onHand, i.unit, i.unitName) : '—'}</span>
                {i.reorderLevel > 0 ? <span>· reorder at {formatQty(i.reorderLevel, i.unit, i.unitName)}</span> : null}
                <span>· {CATEGORY_LABEL[i.category]}</span>
                {i.location ? <span>· kept in {i.location}</span> : null}
                {i.category === 'FOOD_DRINK' ? <span>· used in {i.usedIn} dish size{i.usedIn === 1 ? '' : 's'}</span> : null}
              </div>
              <div className="row">
                {can('stock.record') ? (
                  <>
                    <Button onClick={() => setMove({ ing: i, reason: 'RECEIPT' })}>Delivery</Button>
                    <Button onClick={() => setMove({ ing: i, reason: 'USE' })}>Used</Button>
                    <Button onClick={() => setMove({ ing: i, reason: 'WASTE' })}>{i.category === 'FOOD_DRINK' ? 'Waste' : 'Broken or lost'}</Button>
                  </>
                ) : null}
                {can('stock.manage') ? <Button onClick={() => setMove({ ing: i, reason: 'ADJUSTMENT' })}>Count</Button> : null}
                <Button variant="ghost" onClick={() => setHistory(i)}>History</Button>
                {can('stock.manage') ? (
                  <Button variant="ghost" onClick={() => setEditing(i)}>
                    Edit
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
      <NewItem open={adding} onClose={() => setAdding(false)} />
      <EditItem ing={editing} onClose={() => setEditing(null)} />
      <MoveDialog target={move} onClose={() => setMove(null)} />
      <HistoryDialog ing={history} onClose={() => setHistory(null)} />
    </>
  );
}

function NewItem({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [category, setCategory] = useState<StockCategory>('FOOD_DRINK');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState<StockUnit>('GRAM');
  const [word, setWord] = useState('pieces');
  const [place, setPlace] = useState('');
  const [opening, setOpening] = useState('');
  const [reorder, setReorder] = useState('');
  const make = useStockMutation((body: Record<string, unknown>) => api('/stock/ingredients', { method: 'POST', body }));
  const food = category === 'FOOD_DRINK';
  const shown = food ? UNIT_LABEL[unit] : word.trim() || 'pieces';
  const valid = name.trim().length >= 2 && whole(opening) !== null && whole(reorder) !== null && (food || word.trim().length >= 1);
  const reset = () => {
    setName('');
    setOpening('');
    setReorder('');
    setPlace('');
    setWord('pieces'); // never carry "sets" over to the next item
  };
  return (
    <Modal open={open} onClose={onClose} title="Add an item">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          make.mutate(
            {
              name: name.trim(),
              category,
              unit: food ? unit : 'PIECE',
              ...(food ? {} : { unitName: word.trim() }),
              ...(place.trim() ? { location: place.trim() } : {}),
              // for a kitchen ingredient, empty means none; for anything else, empty means "not counted yet"
              ...(opening.trim() !== '' || food ? { opening: whole(opening) } : {}),
              reorderLevel: whole(reorder),
            },
            { onError: report, onSuccess: () => { toast.ok(`${name.trim()} added`); reset(); onClose(); } },
          );
        }}
      >
        <Field id="sk" label="Kind of item" hint={CATEGORY_HINT[category]}>
          <select id="sk" className="select" value={category} onChange={(e) => { setCategory(e.target.value as StockCategory); setWord('pieces'); }}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field id="sn" label="Name"><input id="sn" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder={food ? 'e.g. Rice' : 'e.g. Beer glass'} /></Field>
        {food ? (
          <Field id="su" label="Counted in" hint="Use the smallest unit so every amount is a whole number: grams, millilitres or pieces">
            <select id="su" className="select" value={unit} onChange={(e) => setUnit(e.target.value as StockUnit)}>
              <option value="GRAM">Grams (g)</option>
              <option value="MILLILITRE">Millilitres (ml)</option>
              <option value="PIECE">Pieces</option>
            </select>
          </Field>
        ) : (
          <Field id="sw" label="Counted in" hint="What you count them as: pieces, sets, pairs, boxes, rolls…">
            <input id="sw" className="input" list="stock-words" value={word} onChange={(e) => setWord(e.target.value)} maxLength={20} />
            <datalist id="stock-words">
              {WORDS.map((w) => (
                <option key={w} value={w} />
              ))}
            </datalist>
          </Field>
        )}
        <Field id="sp" label="Where it is kept (optional)"><input id="sp" className="input" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="e.g. Housekeeping store, Bar" maxLength={40} /></Field>
        <div className="grid-2">
          <Field id="so" label={`Quantity now (${shown})`} hint={food ? undefined : 'Leave empty if it has not been counted yet'}><input id="so" className="input" inputMode="numeric" value={opening} onChange={(e) => setOpening(e.target.value)} placeholder={food ? '0' : ''} /></Field>
          <Field id="sr" label={`Reorder at (${shown})`}><input id="sr" className="input" inputMode="numeric" value={reorder} onChange={(e) => setReorder(e.target.value)} placeholder="0" /></Field>
        </div>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Add</Button>
        </div>
      </form>
    </Modal>
  );
}

function EditItem({ ing, onClose }: { ing: Ingredient | null; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [category, setCategory] = useState<StockCategory>('OTHER');
  const [word, setWord] = useState('');
  const [place, setPlace] = useState('');
  const [reorder, setReorder] = useState('');
  useEffect(() => {
    if (!ing) return;
    setCategory(ing.category);
    setWord(ing.unitName ?? '');
    setPlace(ing.location ?? '');
    setReorder(String(ing.reorderLevel));
  }, [ing]);
  const save = useStockMutation((body: Record<string, unknown>) => api(`/stock/ingredients/${ing!.id}`, { method: 'PATCH', body }));
  if (!ing) return <Modal open={false} onClose={onClose} title="Edit">{null}</Modal>;
  const valid = whole(reorder) !== null;
  return (
    <Modal open onClose={onClose} title={`Edit ${ing.name}`}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          save.mutate({ category, location: place.trim(), unitName: category === 'FOOD_DRINK' ? '' : word.trim(), reorderLevel: whole(reorder) }, { onError: report, onSuccess: () => { toast.ok('Saved'); onClose(); } });
        }}
      >
        <Field id="ek" label="Kind of item" hint={ing.usedIn > 0 ? 'Used in recipes, so it stays food and drink.' : CATEGORY_HINT[category]}>
          <select id="ek" className="select" value={category} disabled={ing.usedIn > 0} onChange={(e) => setCategory(e.target.value as StockCategory)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
        {category !== 'FOOD_DRINK' ? (
          <Field id="ew" label="Counted in">
            <input id="ew" className="input" list="stock-words" value={word} onChange={(e) => setWord(e.target.value)} maxLength={20} />
          </Field>
        ) : null}
        <Field id="ep" label="Where it is kept"><input id="ep" className="input" value={place} onChange={(e) => setPlace(e.target.value)} maxLength={40} /></Field>
        <Field id="er" label={`Reorder at (${unitWord(ing.unit, category === 'FOOD_DRINK' ? null : word.trim() || ing.unitName)})`}><input id="er" className="input" inputMode="numeric" value={reorder} onChange={(e) => setReorder(e.target.value)} /></Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function MoveDialog({ target, onClose }: { target: { ing: Ingredient; reason: Move } | null; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    setQty('');
    setNote('');
  }, [target]);
  const send = useStockMutation((body: Record<string, unknown>) => api(`/stock/ingredients/${target!.ing.id}/movements`, { method: 'POST', body }));
  if (!target) return <Modal open={false} onClose={onClose} title="Stock">{null}</Modal>;
  const { ing, reason } = target;
  const word = unitWord(ing.unit, ing.unitName);
  const food = ing.category === 'FOOD_DRINK';
  const n = /^\d+$/.test(qty.trim()) ? Number(qty.trim()) : null;
  const needsNote = reason === 'WASTE' || reason === 'ADJUSTMENT';
  const valid = n !== null && (reason === 'ADJUSTMENT' ? true : n > 0) && (!needsNote || note.trim().length >= 3);
  const title = reason === 'RECEIPT' ? `Delivery of ${ing.name}` : reason === 'USE' ? `Used or issued: ${ing.name}` : reason === 'WASTE' ? `${food ? 'Waste' : 'Broken or lost'}: ${ing.name}` : `Count ${ing.name}`;
  return (
    <Modal open onClose={onClose} title={title}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          const body = reason === 'ADJUSTMENT' ? { reason, counted: n, note: note.trim() } : { reason, quantity: n, ...(note.trim() ? { note: note.trim() } : {}) };
          send.mutate(body, { onError: report, onSuccess: () => { toast.ok('Recorded'); onClose(); } });
        }}
      >
        {reason === 'ADJUSTMENT' ? <p className="small muted">{ing.counted ? `The system has ${formatQty(ing.onHand, ing.unit, ing.unitName)}. Enter what is really there; the difference is recorded with your reason.` : 'This has not been counted yet. Count what is really there and enter it.'}</p> : null}
        {reason === 'USE' || reason === 'WASTE' ? <p className="small muted">{ing.counted ? `There are ${formatQty(ing.onHand, ing.unit, ing.unitName)} now.` : 'This has not been counted yet: count it first.'}</p> : null}
        <Field id="mq" label={reason === 'ADJUSTMENT' ? `Counted (${word})` : `Quantity (${word})`}>
          <input id="mq" className="input" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus />
        </Field>
        <Field id="mn" label={needsNote ? 'Reason' : 'Note (optional)'}>
          <input id="mn" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason === 'WASTE' ? (food ? 'e.g. spoiled, spilled' : 'e.g. broken, lost, stained') : reason === 'ADJUSTMENT' ? 'e.g. monthly stock take' : reason === 'USE' ? 'e.g. rooms 1 to 6, bar restock' : 'e.g. supplier invoice number'} />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={send.isPending}>Record</Button>
        </div>
      </form>
    </Modal>
  );
}

function HistoryDialog({ ing, onClose }: { ing: Ingredient | null; onClose: () => void }) {
  const q = useMovements(ing?.id ?? null);
  return (
    <Modal open={!!ing} onClose={onClose} title={ing ? `${ing.name}: history` : 'History'}>
      {q.isLoading ? (
        <Loading rows={3} />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : (q.data ?? []).length === 0 ? (
        <p className="muted">Nothing recorded yet.</p>
      ) : (
        <div className="list">
          {(q.data ?? []).map((m) => (
            <div key={m.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{REASON[m.reason]}</span>
                <span className="num">{m.delta > 0 ? '+' : ''}{m.delta.toLocaleString('en-GH')}</span>
              </div>
              <div className="row small muted">
                <span>{formatDateTime(m.at)}</span>
                <span>· {m.by}</span>
                <span>· left {m.balanceAfter.toLocaleString('en-GH')}</span>
              </div>
              {m.note ? <div className="hint">{m.note}</div> : null}
            </div>
          ))}
        </div>
      )}
      <Button block onClick={onClose}>Close</Button>
    </Modal>
  );
}

/* ---------------------------------------------------------------- recipes */
function RecipesTab() {
  const menu = useMenu(true);
  const ingredients = useIngredients();
  const toast = useToast();
  const report = useReport();
  const sizes = (menu.data ?? []).flatMap((c) => c.items.flatMap((i) => i.variants.map((v) => ({ id: v.id, label: i.variants.length > 1 ? `${i.name} · ${v.name}` : i.name }))));
  const [variantId, setVariantId] = useState('');
  const recipe = useRecipe(variantId);
  const [lines, setLines] = useState<{ ingredientId: string; quantity: string }[]>([]);
  const [add, setAdd] = useState('');
  const save = useStockMutation((body: { lines: { ingredientId: string; quantity: number }[] }) => api(`/menu/variants/${variantId}/recipe`, { method: 'PUT', body }));

  useEffect(() => {
    if (recipe.data) setLines(recipe.data.map((l) => ({ ingredientId: l.ingredientId, quantity: String(l.quantity) })));
  }, [recipe.data]);
  const byId = new Map((ingredients.data ?? []).map((i) => [i.id, i]));
  const parsed = lines.map((l) => ({ ingredientId: l.ingredientId, quantity: /^\d+$/.test(l.quantity.trim()) ? Number(l.quantity.trim()) : 0 }));
  const valid = parsed.every((l) => l.quantity > 0);
  // a recipe uses food and drink only: a glass or a bed sheet is never taken off the shelf by an order
  const free = (ingredients.data ?? []).filter((i) => i.active && i.category === 'FOOD_DRINK' && !lines.some((l) => l.ingredientId === i.id));

  return (
    <div className="stack">
      <p className="small muted">Say how much of each ingredient one portion uses. When an order is sent, that much comes off the stock, and a dish with an ingredient that has run out can no longer be ordered. A dish with no recipe is not tracked. Only food and drink items can be used here.</p>
      <Field id="rd" label="Dish and size">
        <select id="rd" className="select" value={variantId} onChange={(e) => setVariantId(e.target.value)}>
          <option value="">Choose a dish…</option>
          {sizes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </Field>
      {variantId ? (
        recipe.isLoading ? (
          <Loading rows={2} />
        ) : (
          <div className="stack">
            {lines.length === 0 ? <p className="small muted">No recipe yet: this dish is not tracked.</p> : null}
            {lines.map((l, idx) => {
              const ing = byId.get(l.ingredientId);
              return (
                <div key={l.ingredientId} className="row" style={{ alignItems: 'flex-end' }}>
                  <Field id={`rq-${idx}`} label={`${ing?.name ?? 'Ingredient'} (${ing ? UNIT_LABEL[ing.unit] : ''})`}>
                    <input id={`rq-${idx}`} className="input" inputMode="numeric" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => (j === idx ? { ...x, quantity: e.target.value } : x)))} />
                  </Field>
                  <Button variant="ghost" aria-label={`Remove ${ing?.name ?? 'ingredient'}`} onClick={() => setLines(lines.filter((_, j) => j !== idx))}>✕</Button>
                </div>
              );
            })}
            {free.length > 0 ? (
              <Field id="ra" label="Add an ingredient">
                <select id="ra" className="select" value={add} onChange={(e) => { if (e.target.value) { setLines([...lines, { ingredientId: e.target.value, quantity: '' }]); } setAdd(''); }}>
                  <option value="">Choose…</option>
                  {free.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </Field>
            ) : null}
            <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate({ lines: parsed }, { onError: report, onSuccess: () => toast.ok('Recipe saved') })}>Save recipe</Button>
          </div>
        )
      ) : null}
    </div>
  );
}
